import { useMemo, useEffect, useState } from 'react';
import { Button, useQuery, useI18n, useNavigate, useMutation, useQueryState } from '@ywwwtseng/react-kit';
import { Typography, useShare } from '@ywwwtseng/tma-react-kit';
import * as schema from '@/db/schema';
import { useShareUrl } from '@/hooks/useShareUrl';
import { Chat } from '@/components/common/Chat';
import { JoinChatModal } from '@/components/common/JoinChatModal';
import type { Gift, SelectTgChat } from '@/types';

export default function ChatGrowthGiftScreen({
  params,
}: {
  params: { gift_id: string, invite_code?: string };
}) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const me = useQueryState<typeof schema.users.$inferSelect>('me');
  const [showJoinGroupModal, setShowJoinGroupModal] = useState(false);
  const { mutate, isLoading } = useMutation('gift:claim', {
    ignoreNotify: (error) => {
      return error.error_code === 40001;
    },
    onError(error) {
      if (error.error_code === 40001) {
        setShowJoinGroupModal(true);
      }
    },
  });
  const { mutate: recordMembership } = useMutation('chat:record-membership');
  const share = useShare();
  const { direct_url, invite_code } = useShareUrl();
  const { data: gift } = useQuery<Gift>('gifts', {
    params: {
      id: params.gift_id,
    },
  });

  // invite_code 是 undefined 时，表示没有邀请码，使用者 chat 看到這個資訊，代表使用者已經加入 chat 了，更新 user_chat_memberships 表
  useEffect(() => {
    if (!params.invite_code && gift?.tg_chat_id) {
      // 用户已经在 chat 中看到这个页面，记录到数据库
      void recordMembership({
        chat_id: gift.tg_chat_id,
      });
    }
  }, [params.invite_code, gift?.tg_chat_id]);


  const { data: tg_chat } = useQuery<SelectTgChat>('chat', {
    params: {
      id: gift?.tg_chat_id,
    },
    enabled: !!gift?.tg_chat_id,
  });

  const submitLabel = useMemo(() => {
    if (gift?.status === 'active') {
      if (params.invite_code) {
        return t('common.claim');
      }
      return t('common.share');
    }

    return t('common.ended');
  }, [invite_code, gift]);

  if (!gift) {
    return null;
  }

  return (
    <>
      <div className="flex flex-col justify-start w-full h-full gap-2 py-3 px-5">
        <div className="flex flex-col items-start w-full gap-2">
          <Typography size="1" weight={500}>
            {t('common.join_chat_to_earn')}
          </Typography>
        </div>

        {tg_chat && (
          <Chat chat={tg_chat} chriveRight />
        )}

        <div className="flex flex-col justify-start w-full flex-1 gap-2">
          {/* $MOONINI Progress Card */}
          <div className="card flex flex-col justify-start w-full gap-2">

            <Typography size="2" whitespacePreWrap overflowWrap="break-word">
              {gift.description}
            </Typography>
          </div>

          <div className="w-full mt-auto pb-2">
            <Button
              disabled={gift.status !== 'active'}
              isLoading={isLoading}
              width="full"
              color="primary"
              variant="contained"
              size="sm"
              rounded="full"
              onClick={() => {
                if (params.invite_code) {
                  void mutate({
                    type: 'invite_code',
                    gift_id: params.gift_id,
                    invite_code: params.invite_code,
                    language_code: me?.language_code,
                  }).then((result) => {
                    if (result.ok) {
                      navigate('Home');
                    }
                  });
                } else if (invite_code && gift.id) {
                  const url = `${direct_url}?startapp=c_${gift.id}_${invite_code}`;

                  share({
                    url,
                    text: t(gift.description),
                  });
                }
              }}
            >
              {submitLabel}
            </Button>
          </div>
        </div>
      </div>

      <JoinChatModal
        open={showJoinGroupModal}
        onClose={() => setShowJoinGroupModal(false)}
        tg_chat={tg_chat}
      />
    </>
  );
}
